// src/api/http.js
import axios from "axios";
import { TokenService } from "./tokenService";
import { API_ENDPOINTS } from "./endpoints";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  // ❌ DO NOT set Content-Type globally
  // Axios must decide per-request (JSON vs FormData)
});

/* ============================
   REQUEST INTERCEPTOR
   - Attach access token
   - Fix Content-Type for FormData
============================ */
api.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ✅ If sending FormData, DO NOT set Content-Type at all
    // Axios will set: multipart/form-data; boundary=...
    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    if (isFormData) {
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    } else {
      // ✅ For normal JSON requests, set JSON content-type
      config.headers = config.headers ?? {};
      // Only set if caller didn't already set something else
      if (!config.headers["Content-Type"] && !config.headers["content-type"]) {
        config.headers["Content-Type"] = "application/json";
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ============================
   RESPONSE INTERCEPTOR
   - Refresh token on 401
============================ */
let isRefreshing = false;
let pendingRequests = [];

const processQueue = (error, token = null) => {
  pendingRequests.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  pendingRequests = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({
          resolve: (token) => {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = TokenService.getRefreshToken();
      if (!refreshToken) {
        TokenService.clearTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      const refreshResponse = await axios.post(
        process.env.REACT_APP_BACKEND_URL + API_ENDPOINTS.REFRESH_TOKEN,
        { refreshToken },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const newAccessToken =
        refreshResponse.data?.data?.accessToken ?? refreshResponse.data?.accessToken;

      const newRefreshToken =
        refreshResponse.data?.data?.refreshToken ?? refreshResponse.data?.refreshToken;

      TokenService.setTokens(newAccessToken, newRefreshToken);

      // Set default Authorization only (NOT content-type)
      api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

      processQueue(null, newAccessToken);

      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      // IMPORTANT: if original request was FormData, ensure no JSON content-type leaks
      const isFormData =
        typeof FormData !== "undefined" && originalRequest.data instanceof FormData;

      if (isFormData) {
        delete originalRequest.headers["Content-Type"];
        delete originalRequest.headers["content-type"];
      }

      return api(originalRequest);
    } catch (refreshErr) {
      processQueue(refreshErr, null);
      TokenService.clearTokens();
      window.location.href = "/login";
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
